import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Box, useTheme, Flex, Image, VStack, AvatarGroup} from '@chakra-ui/react';
import OfferImage from '../../../../assets/images/offerImage.png';
import {TextG10, TextG12, TextG14, TextG16} from '../../../typography';
import {Offer, OfferType, Place, Event} from '../../../../types/article';
import Card from '../../../Card';
import {getOfferDate, isFutureOffer} from '../../../../utils/date';
import useBookmark from '../../../../hooks/useBookmark';
import {getImageURL} from '../../../../constants/imageStorage';
import {useTrackVisibility} from 'react-intersection-observer-hook';
import useVisibleArticles from '../../../../hooks/useVisible';
import {has} from 'lodash';
import dayjs from 'dayjs';
import {useSearchParams} from 'react-router-dom';
import CardInterestTags from '../../CardInterestTags';
import {BookmarkElement} from '../../index';
import useOfferModal from '../../../../hooks/useOfferModal';
import styled from 'styled-components';
import Icon from '../../../Icon';
import {useFetchEvent} from '../../../../hooks/useEvent';
import useAuth from '../../../../hooks/useAuth';
import ExclusiveCommunityLabel from '../../../ExclusiveCommunityLabel';
import PremiumOfferCard from './PremiumOfferCard';
import useTrackEvent from '../../../../hooks/useTrackEvent';
import {UsersListModal} from '../../../UsersList';
import {useFetchOfferBookmarkedUsers} from '../../../../hooks/useOffers';
import UserAvatar from '../../../UserAvatar';

interface OfferCardProps {
  offer?: Offer;
  fullCard?: boolean;
  rightBottomItem?: any;
  place?: Place;
  event?: Event;
  hideActive?: boolean;
  hideCard?: boolean;
  interestsScrollable?: boolean;
  height?: string;
  isFeatured?: boolean;
  showBookmark?: boolean;
  onClick?: () => void;
  showExclusive?: boolean;
}

export const getOfferCardColor = (offerType: OfferType) => {
  switch (offerType) {
    case OfferType.INDIVIDUAL_REDEMPTION:
      return '#329241';
    case OfferType.REDEMPTION:
      return '#F5737F';
    case OfferType.PREMIUM_REDEMPTION:
      return '#E02F84';
    case OfferType.POINTS_BOOST:
      return '#8A44E3';
    case OfferType.EVENT_REDEMPTION:
      return '#272929';
    default:
      return '#8A44E3';
  }
};

const OverflowDiv = styled.div`
  position: absolute;
  background-color: rgba(0, 0, 0, 0.5);
  width: 100%;
  min-height: 125px;
  z-index: 99;
  border-radius: 12px;
  display: flex;
  cursor: pointer;
  -webkit-padding-start: var(--chakra-space-3);
  padding-inline-start: var(--chakra-space-3);
  -webkit-padding-end: var(--chakra-space-3);
  padding-inline-end: var(--chakra-space-3);
  padding-top: var(--chakra-space-3);
  padding-bottom: var(--chakra-space-3);
`;

const MembershipLockOverlay = styled.div`
  position: absolute;
  top: 6px;
  background: rgba(0, 0, 0, 0.5);
  width: 100%;
  height: calc(100% - 7px);
  z-index: 99;
  border-radius: 12px;
  display: flex;
  cursor: pointer;
  -webkit-padding-start: var(--chakra-space-3);
  padding-inline-start: var(--chakra-space-3);
  -webkit-padding-end: var(--chakra-space-3);
  padding-inline-end: var(--chakra-space-3);
  padding-top: var(--chakra-space-3);
  padding-bottom: var(--chakra-space-3);
`;

const StyledTitle = styled(TextG14)`
  font-size: ${(props) => (props.$isFeatured ? '18px !important' : '14px')};
  font-weight: ${(props) => (props.$isFeatured ? '700' : '500')};
`;

const LockBadge = styled.div<{bgColor?: string}>`
  position: absolute;
  top: -8px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  background: ${(props) => props.bgColor || '#E02F84'};
  color: white;
  padding: 5px 18px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  white-space: nowrap;
  cursor: pointer;
  font-weight: 700;
  font-size: 12px;
`;

export const checkIsOfferLocked = (offerId: number, event?: Event, offer?: Offer, user?: any) => {
  // Check if offer hasn't started yet (future offer)
  const isFuture = offer && isFutureOffer(offer);
  const unlockDate = isFuture ? offer.starts_at : null;

  // Check event-based locking
  let isEventLocked = false;
  if (event?.unlocks_offer_id === offerId) {
    if (event?.unlocked_offer) {
      isEventLocked = !(
        event?.unlocked_offer.offer_id === offerId && event?.unlocks_offer_id === offerId
      );
    } else {
      isEventLocked = true;
    }
  }

  // Check membership-based locking
  let isMembershipLocked = false;
  if (offer) {
    // If offer is private and has exclusivity, check membership
    if (offer.is_private && offer.community_membership_plan?.has_offer_exclusivity) {
      // A user individually granted access is never membership-locked.
      isMembershipLocked = !user?.hasActiveMembership && !offer.has_individual_access;
    }
  }

  // Determine lock type priority: future > event > membership
  let lockType: 'future' | 'event' | 'membership' | null = null;
  if (isFuture) {
    lockType = 'future';
  } else if (isEventLocked) {
    lockType = 'event';
  } else if (isMembershipLocked) {
    lockType = 'membership';
  }

  // Return detailed lock information
  return {
    isLocked: isFuture || isEventLocked || isMembershipLocked,
    isEventLocked,
    isMembershipLocked,
    isFutureLocked: isFuture,
    unlockDate,
    lockType,
  };
};

const OfferCard = ({
  offer,
  fullCard = true,
  rightBottomItem,
  place,
  event,
  hideActive = false,
  hideCard = false,
  interestsScrollable = false,
  height,
  isFeatured = false,
  showBookmark = true,
  onClick,
  showExclusive = true,
}: OfferCardProps) => {
  if (!offer || offer.offer_type === undefined) return null;
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const title = useRef<HTMLDivElement>(null);
  const [hide, setHide] = useState(hideCard);
  const {isBookmarked, handleOnBookmarkClick} = useBookmark(offer as Offer, 'offer');
  const {showOfferModal} = useOfferModal();
  const {addUserEvent} = useTrackEvent();
  const {user} = useAuth();
  const isExclusive = offer?.communities?.length > 0 && offer.is_private;
  const [offerEvent, setOfferEvent] = useState<Event | undefined>(event);
  const [lockInfo, setLockInfo] = useState(checkIsOfferLocked(offer.id, offerEvent, offer, user));
  const [showUsersModal, setShowUsersModal] = useState(false);
  const bookmarksCount = offer?.bookmarks_count || 0;
  const {data: bookmarkedUsersData} = useFetchOfferBookmarkedUsers(offer.id, bookmarksCount > 0);
  const bookmarkedUsers = bookmarkedUsersData?.pages[0]?.data?.data || [];

  const {data: lazyLoadadedEvent} =
    !onClick && offer?.event_id ? useFetchEvent(offer.event_id, !onClick) : {data: null};
  useEffect(() => {
    if (
      (!offerEvent ||
        (offerEvent && has(offerEvent, 'unlocks_offer_id') && has(offerEvent, 'unlocked_offer'))) &&
      lazyLoadadedEvent
    ) {
      const fetchedOffer = lazyLoadadedEvent?.data;
      setOfferEvent(fetchedOffer);
      setLockInfo(checkIsOfferLocked(offer.id, fetchedOffer, offer, user));
    }
  }, [lazyLoadadedEvent, user]);

  // Update locked state when user changes
  useEffect(() => {
    setLockInfo(checkIsOfferLocked(offer.id, offerEvent, offer, user));
  }, [user?.hasActiveMembership, offer.id, offerEvent, offer]);

  const datePassed = useMemo(() => dayjs().utc().isAfter(dayjs.utc(offer?.ends_at)), [offer]);
  const futureOffer = useMemo(() => isFutureOffer(offer), [offer]);

  const [ref, {wasEverVisible}] = useTrackVisibility();
  const {addVisibleArticle} = useVisibleArticles();

  useEffect(() => {
    if (!!searchParams.get('offer') && searchParams.get('offer') == offer.id.toString()) {
      showOfferModal({offer});
      if (!!searchParams.get('bookmark') && !isBookmarked) {
        handleOnBookmarkClick(null);
      }
    }
  }, [searchParams]);

  // useEffect(() => {
  //   if (hideActive && offer.countdown_required)
  //     if (some(activeRedemptions, (redemption) => redemption.offer.id === offer?.id)) {
  //       setHide(true);
  //     } else {
  //       setHide(false);
  //     }
  // }, [activeRedemptions, hideActive]);

  useEffect(() => {
    if (wasEverVisible && offer?.id.toString()) {
      addVisibleArticle({id: offer.id.toString(), type: 'offer'});
    }
  }, [wasEverVisible]);

  if (!isFeatured) {
    if (offer.is_premium) return <PremiumOfferCard showExclusive={showExclusive} showBookmark={showBookmark} offer={offer} />;
  }
  const offerTypePoints = ((offer?.offer_type == OfferType.POINTS_BOOST &&
    offer?.points_earning !== null) ||
    offer?.offer_type !== OfferType.POINTS_BOOST) && (
    <Box
      py={1}
      px={1}
      pos="absolute"
      top={offer?.offer_type == OfferType.POINTS_BOOST ? 0 : ''}
      left={offer?.offer_type == OfferType.POINTS_BOOST ? 0 : ''}
      bottom={offer?.offer_type == OfferType.POINTS_BOOST ? '' : 0}
      right={offer?.offer_type == OfferType.POINTS_BOOST ? '' : 0}
      borderTopLeftRadius="10px"
      borderBottomRightRadius="10px"
      background={offer?.offer_type == OfferType.POINTS_BOOST ? 'green' : theme.colors.rose}>
      <Flex>
        <TextG10
          fontWeight="700"
          color={offer?.offer_type == OfferType.POINTS_BOOST ? 'white' : theme.colors.white}>
          {offer?.offer_type == OfferType.POINTS_BOOST
            ? `+${offer?.points_earning.toLocaleString()}`
            : offer?.points_redeem_amount.toLocaleString() == '0'
            ? 'Free'
            : offer?.points_redeem_amount.toLocaleString()}
        </TextG10>
      </Flex>
    </Box>
  );

  const offerCardContent = isFeatured ? (
    <Card
      article={offer}
      childTop={isFeatured ? `calc(77% - ${(title.current?.clientHeight || 24) / 2}px)` : ''}
      overlayBackgroundColor={isFeatured ? '#2729295C' : ''}
      overlayHeight={isFeatured ? '33%' : ''}
      borderRadius={isFeatured ? '0px' : '12px'}
      height={height}
      backgroundColor={offer?.offer_type && getOfferCardColor(offer.offer_type)}
      childPaddingX={!fullCard ? 3 : 4}>
      <VStack alignItems={'left'} ref={title}>
        <StyledTitle
          wordBreak="break-word"
          fontWeight="700"
          color={theme.colors.white}
          noOfLines={2}
          $isFeatured={isFeatured}>
          {offer?.title}
        </StyledTitle>
        {futureOffer ? (
          <TextG10 fontWeight="700" color={theme.colors.white} isTruncated="&hellip;" noOfLines={1}>
            Available {getOfferDate(offer?.starts_at)}
          </TextG10>
        ) : (
          <TextG10 fontWeight="700" color={theme.colors.white} isTruncated="&hellip;" noOfLines={1}>
            {`${datePassed ? 'Expired on' : 'Expires'} ${getOfferDate(offer?.ends_at)}`}
          </TextG10>
        )}
      </VStack>
    </Card>
  ) : (
    <>
      {!hide && (
        <>
          {isExclusive && showExclusive ? (
            <ExclusiveCommunityLabel community={offer.communities[0]} />
          ) : null}

          <Box
            position="relative"
            ref={ref}
            px={fullCard ? 3 : 2}
            // Extra top padding when a LockBadge is rendered: the badge is positioned at
            // top: -8px with ~32px height, so it overlaps the first ~24px of the card. Without
            // this we clip the title's first line.
            pt={fullCard ? (lockInfo.isLocked ? 6 : 3) : 2}
            pb={fullCard ? 3 : 2}
            borderRadius="12px"
            background={offer?.offer_type && getOfferCardColor(offer.offer_type)}
            // minH (not a fixed height) so a long title can push the card taller if needed.
            // 160px gives room for a 2-line title + date + place + neighbors row when locked.
            minH={height ? height : fullCard ? '160px' : '45vw'}
            width="100%"
            onClick={() => {
              onClick ? onClick() : showOfferModal({offer});

              addUserEvent({
                type: 'click',
                event_name: 'card_click',
                event_time: dayjs().format(),
                data: {itemType: 'offer', itemId: offer.id},
              });
            }}>
            {/* Sold out overlay */}
            {offer?.quantity_available === 0 ? (
              <Box
                position="absolute"
                top="0"
                left="0"
                right="0"
                bottom="0"
                backgroundColor="rgba(0, 0, 0, 0.75)"
                borderRadius="12px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                zIndex="2">
                <Box
                  background="linear-gradient(135deg, #6c757d, #5a6268)"
                  px="4"
                  py="2"
                  borderRadius="8px"
                  border="2px solid white"
                  boxShadow="0 4px 12px rgba(0, 0, 0, 0.3)">
                  <TextG12
                    fontWeight="800"
                    color="white"
                    textShadow="1px 1px 2px rgba(0, 0, 0, 0.5)"
                    letterSpacing="1px">
                    UNAVAILABLE
                  </TextG12>
                </Box>
              </Box>
            ) : (
              offer?.quantity_available !== null &&
              offer.quantity_available <= 99 && (
                <Box position={'absolute'} bottom={3} right={'8px'} zIndex={2}>
                  <TextG12 color="white">{offer?.quantity_available} Available</TextG12>
                </Box>
              )
            )}
            <Flex
              gap={fullCard ? 2 : 1}
              w="100%"
              h="100%"
              userSelect="none"
              flexDir={fullCard ? 'row' : 'column'}>
              <Box maxW={fullCard ? '100px' : '100%'} flex="none" pos="relative">
                <Image
                  fallbackStrategy="onError"
                  w={fullCard ? '100px' : '100%'}
                  borderRadius="10px"
                  objectFit="cover"
                  h={fullCard ? '100%' : '60px'}
                  src={offer?.image_url ? getImageURL(offer.image_url) : OfferImage}
                />
                {offer?.retail_price && parseFloat(offer.retail_price) > 0 && (
                  <Box
                    py={1}
                    px={1.5}
                    pos="absolute"
                    top={0}
                    left={0}
                    borderTopLeftRadius="10px"
                    borderBottomRightRadius="10px"
                    background="rgba(255, 255, 255, 0.95)"
                    boxShadow="0 2px 4px rgba(0,0,0,0.2)">
                    <TextG10 fontWeight="700" color="#272929">
                      ${parseFloat(offer.retail_price).toFixed(0)} value
                    </TextG10>
                  </Box>
                )}
                {offerTypePoints}
              </Box>
              <VStack
                align="flex-start"
                color={theme.colors.white}
                px={fullCard ? 2 : 0}
                position="relative"
                flex="1">
                {fullCard ? (
                  <TextG16
                    textAlign="left"
                    pr={'36px'}
                    wordBreak="break-word"
                    fontWeight="700"
                    color={theme.colors.white}
                    noOfLines={2}>
                    {offer?.title}
                  </TextG16>
                ) : futureOffer && getOfferDate(offer?.starts_at) ? (
                  <TextG10
                    mt={'5px'}
                    fontWeight="500"
                    color={theme.colors.white}
                    isTruncated="&hellip;"
                    noOfLines={2}>
                    Available{' '}
                    {getOfferDate(offer?.starts_at)?.includes('Tomorrow') ||
                    getOfferDate(offer?.starts_at)?.includes('Today')
                      ? getOfferDate(offer?.starts_at)?.toLocaleLowerCase()
                      : `${getOfferDate(offer?.starts_at)}`}
                  </TextG10>
                ) : getOfferDate(offer?.ends_at) ? (
                  <TextG10
                    mt={'5px'}
                    fontWeight="500"
                    color={theme.colors.white}
                    isTruncated="&hellip;"
                    noOfLines={2}>
                    {`${datePassed ? 'Expired on ' : 'Expires '} ${
                      getOfferDate(offer?.ends_at)?.includes('Tomorrow') ||
                      getOfferDate(offer?.ends_at)?.includes('Today')
                        ? getOfferDate(offer?.ends_at)?.toLocaleLowerCase()
                        : `${getOfferDate(offer?.ends_at)}`
                    }`}
                  </TextG10>
                ) : null}

                {!fullCard ? (
                  <TextG12
                    wordBreak="break-word"
                    fontWeight="700"
                    color={theme.colors.white}
                    noOfLines={2}>
                    {offer?.title}
                  </TextG12>
                ) : futureOffer && getOfferDate(offer?.starts_at) ? (
                  <TextG10
                    fontWeight="700"
                    color={theme.colors.white}
                    isTruncated="&hellip;"
                    noOfLines={1}>
                    Available {getOfferDate(offer?.starts_at)}
                  </TextG10>
                ) : getOfferDate(offer?.ends_at) ? (
                  <TextG10
                    fontWeight="700"
                    color={theme.colors.white}
                    isTruncated="&hellip;"
                    noOfLines={1}>
                    {`${datePassed ? 'Expired on' : 'Expires'} ${getOfferDate(offer?.ends_at)}`}
                  </TextG10>
                ) : null}

                {offerEvent && offerEvent.unlocks_offer_id !== null ? (
                  <TextG10 noOfLines={!fullCard ? 1 : 2} color={theme.colors.white}>
                    {offerEvent.title}
                  </TextG10>
                ) : offer?.place ? (
                  <TextG10 noOfLines={!fullCard ? 1 : 2} color={theme.colors.white}>
                    {offer.place.name}
                  </TextG10>
                ) : place?.name ? (
                  <TextG10 noOfLines={!fullCard ? 1 : 2} color={theme.colors.white}>
                    {place.name}
                  </TextG10>
                ) : null}
                {bookmarksCount > 0 && (
                  <Flex
                    align="center"
                    gap={2}
                    alignSelf="flex-start"
                    cursor="pointer"
                    _hover={{opacity: 0.8}}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowUsersModal(true);
                    }}>
                    {/* Only render the avatar group once the async users fetch has returned a non-empty
                        list — otherwise `AvatarGroup` shows an empty wrapper that still consumes the
                        Flex gap (and invisible white-on-pink borders for users without photos), which
                        reads as a mystery margin before the count. */}
                    {bookmarkedUsers.length > 0 && (
                      <AvatarGroup size="xs" max={3} spacing={-2}>
                        {bookmarkedUsers.slice(0, 3).map((user: any, index: number) => (
                          <UserAvatar
                            key={user.user_id || `user-${index}`}
                            user={{
                              id: user.user_id,
                              name: user.name,
                              surname: user.surname,
                              handle: user.handle,
                              profilePhotoUrl: user.profile_photo_url,
                            } as any}
                            size="xs"
                            avatar_image={user.profile_photo_url || undefined}
                            sx={{
                              border: '1.5px solid white',
                              width: '20px',
                              height: '20px',
                            }}
                          />
                        ))}
                      </AvatarGroup>
                    )}
                    <TextG10
                      color={theme.colors.white}
                      fontWeight="500"
                      _hover={{textDecoration: 'underline'}}>
                      {`${bookmarksCount} Neighbor${bookmarksCount !== 1 ? 's' : ''} Interested`}
                    </TextG10>
                  </Flex>
                )}
              </VStack>
              {rightBottomItem && rightBottomItem}
              {showBookmark && <BookmarkElement article={{...offer, type: 'offer'}} />}
            </Flex>
          </Box>
        </>
      )}
    </>
  );
  const getLockBadgeContent = () => {
    if (lockInfo.lockType === 'future') {
      const unlockText = getOfferDate(lockInfo.unlockDate);
      return {
        icon: '🔒',
        text: `Unlocks ${unlockText}`,
        bgColor: '#272929',
      };
    } else if (lockInfo.lockType === 'membership') {
      return {
        icon: '🔒',
        text: 'Special Perk for Insiders',
        bgColor: '#272929',
      };
    } else if (lockInfo.lockType === 'event') {
      return {
        icon: '🔒',
        text: 'Check-In to Unlock',
        bgColor: '#272929',
      };
    }
    return null;
  };

  const lockBadgeContent = getLockBadgeContent();
  const showSpecialForYouBadge = !!offer.has_individual_access && !lockInfo.isLocked;

  return (
    <Box w={'100%'} pos={'relative'} zIndex={10}>
      {lockInfo.isLocked && lockBadgeContent && (
        <>
          <LockBadge
            bgColor={lockBadgeContent.bgColor}
            onClick={() => {
              onClick ? onClick() : showOfferModal({offer});

              addUserEvent({
                type: 'click',
                event_name: 'card_click',
                event_time: dayjs().format(),
                data: {itemType: 'offer', itemId: offer.id},
              });
            }}>
            <span style={{fontSize: '14px'}}>{lockBadgeContent.icon}</span>
            <span>{lockBadgeContent.text}</span>
          </LockBadge>
          {(lockInfo.lockType === 'membership' || lockInfo.lockType === 'event') && (
            <MembershipLockOverlay
              onClick={() => {
                onClick ? onClick() : showOfferModal({offer});

                addUserEvent({
                  type: 'click',
                  event_name: 'card_click',
                  event_time: dayjs().format(),
                  data: {itemType: 'offer', itemId: offer.id},
                });
              }}
            />
          )}
        </>
      )}
      {showSpecialForYouBadge && (
        <LockBadge
          bgColor={'#E02F84'}
          onClick={() => {
            onClick ? onClick() : showOfferModal({offer});

            addUserEvent({
              type: 'click',
              event_name: 'card_click',
              event_time: dayjs().format(),
              data: {itemType: 'offer', itemId: offer.id},
            });
          }}>
          <span>Special for you</span>
        </LockBadge>
      )}
      {offerCardContent}
      <UsersListModal
        isOpen={showUsersModal}
        onClose={() => setShowUsersModal(false)}
        title="Neighbors Interested"
        totalCount={bookmarksCount}
        fetchUsersHook={useFetchOfferBookmarkedUsers}
        entityId={offer.id}
      />
    </Box>
  );
};

export default OfferCard;
